import { Link, useParams } from 'react-router-dom';
import { useReveal } from '../lib/anim/useReveal';
import { ENGINES } from '../content/engines/index';
import type { EngineStatus } from '../content/engines/index';
import nexlyticsMd from '../../../../engines/nexlytics-scorer/docs/README.md?raw';

const STATUS_PILL_STYLE: Record<EngineStatus, React.CSSProperties> = {
  live: {
    background: 'var(--color-accent)',
    color: '#FFFFFF',
    border: 'none',
  },
  beta: {
    background: 'rgba(11, 18, 32, 0.06)',
    color: 'var(--color-mute)',
    border: '1px solid var(--color-hairline)',
  },
  'coming-soon': {
    background: 'transparent',
    color: 'var(--color-mute)',
    border: '1px solid var(--color-hairline)',
  },
};

const STATUS_LABEL: Record<EngineStatus, string> = {
  live: 'Live',
  beta: 'Beta',
  'coming-soon': 'Coming soon',
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  const reveal = useReveal({ delay: 1 });
  return (
    <div
      ref={reveal.ref}
      {...reveal.props}
      className={`py-8${reveal.props.className ? ' ' + reveal.props.className : ''}`}
      style={{ borderTop: '1px solid var(--color-hairline)' }}
    >
      <h2 className="text-sm uppercase tracking-widest mb-3 font-medium" style={{ color: 'var(--color-accent)', fontFamily: 'var(--font-mono)' }}>{title}</h2>
      <div className="leading-relaxed" style={{ color: 'var(--color-mute)' }}>{children}</div>
    </div>
  );
}

function NotFound() {
  const reveal = useReveal();
  return (
    <main className="min-h-screen text-ink flex items-center justify-center" style={{ background: 'var(--color-bg)' }}>
      <div
        ref={reveal.ref}
        {...reveal.props}
        className={`max-w-md mx-auto px-6 text-center${reveal.props.className ? ' ' + reveal.props.className : ''}`}
      >
        <p className="text-sm uppercase tracking-widest mb-4" style={{ color: 'var(--color-mute)' }}>Engine not found</p>
        <h1 className="text-3xl font-bold text-ink mb-4" style={{ fontFamily: 'var(--font-display)' }}>
          We don't have that engine.
        </h1>
        <p className="mb-8" style={{ color: 'var(--color-mute)' }}>
          The slug you requested doesn't match any engine in our catalogue.
        </p>
        <Link
          to="/engines"
          className="inline-flex items-center justify-center px-6 py-3 rounded font-medium transition-colors"
          style={{ border: '1px solid var(--color-hairline)', color: 'var(--color-ink)' }}
        >
          ← Back to engines
        </Link>
      </div>
    </main>
  );
}

export function EngineDetail() {
  const { slug } = useParams<{ slug: string }>();
  const engine = ENGINES.find((e) => e.slug === slug);
  const hero = useReveal();

  if (!engine) {
    return <NotFound />;
  }

  return (
    <main className="min-h-screen text-ink" style={{ background: 'var(--color-bg)' }}>
      {/* ── Hero ── */}
      <section className="relative overflow-hidden py-28 md:py-36">
        {/* Ambient glow */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-16 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full blur-3xl"
          style={{ background: 'var(--color-accent-soft)' }}
        />

        <div className="max-w-4xl mx-auto px-6 relative z-10">
          {/* Breadcrumb */}
          <nav className="mb-8" aria-label="Breadcrumb">
            <Link
              to="/"
              className="text-sm transition-colors"
              style={{ color: 'var(--color-mute)' }}
            >
              Home
            </Link>
            <span className="mx-2" style={{ color: 'var(--color-mute)' }} aria-hidden>›</span>
            <Link
              to="/engines"
              className="text-sm transition-colors"
              style={{ color: 'var(--color-mute)' }}
            >
              Engines
            </Link>
            <span className="mx-2" style={{ color: 'var(--color-mute)' }} aria-hidden>›</span>
            <span className="text-sm" style={{ color: 'var(--color-mute)' }}>{engine.name}</span>
          </nav>

          <div
            ref={hero.ref}
            {...hero.props}
            className={hero.props.className}
          >
            {/* Name + status pill */}
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight tracking-tight text-ink" style={{ fontFamily: 'var(--font-display)' }}>
                {engine.name}
              </h1>
              <span
                className="text-sm px-3 py-1 rounded-full font-medium"
                style={{ ...STATUS_PILL_STYLE[engine.status], fontFamily: 'var(--font-mono)' }}
              >
                {STATUS_LABEL[engine.status]}
              </span>
            </div>

            {/* One-liner */}
            <p className="text-xl md:text-2xl font-normal leading-snug" style={{ color: 'var(--color-mute)' }}>
              {engine.oneLiner}
            </p>

            {/* Live demo CTA */}
            {engine.liveDemo && (
              <div className="mt-8">
                <a
                  href={engine.liveDemo}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 rounded font-semibold hover:opacity-90 transition-opacity"
                  style={{ background: 'var(--color-accent)', color: '#FFFFFF' }}
                >
                  Live demo ↗
                </a>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── Detail sections ── */}
      <section className="pb-16">
        <div className="max-w-4xl mx-auto px-6">
          <Section title="What it does">
            <p>{engine.whatItDoes}</p>
          </Section>

          <Section title="What it eats">
            <p>{engine.whatItEats}</p>
          </Section>

          <Section title="What it outputs">
            <p>{engine.whatItOutputs}</p>
          </Section>

          <Section title="Cost per run">
            <p className="font-mono text-lg" style={{ color: 'var(--color-accent)', fontFamily: 'var(--font-mono)' }}>{engine.costPerRun}</p>
          </Section>

          <Section title="Who it's for">
            <p>{engine.whoItsFor}</p>
          </Section>

          <Section title="Example deployments">
            <ul className="space-y-2 mt-1">
              {engine.exampleDeployments.map((dep) => (
                <li key={dep} className="flex items-start gap-3">
                  <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full" style={{ background: 'var(--color-accent)' }} aria-hidden />
                  <span>{dep}</span>
                </li>
              ))}
            </ul>
          </Section>

          {/* Canonical engine README for Nexlytics */}
          {engine.bodyMdSrc === 'engines/nexlytics-scorer/docs/README.md' && (
            <div className="py-8" style={{ borderTop: '1px solid var(--color-hairline)' }}>
              <h2 className="text-sm uppercase tracking-widest mb-6 font-medium" style={{ color: 'var(--color-accent)', fontFamily: 'var(--font-mono)' }}>
                Engine documentation
              </h2>
              <pre className="whitespace-pre-wrap text-sm font-mono leading-relaxed rounded-xl p-6 overflow-x-auto" style={{ background: 'var(--color-bg)', border: '1px solid var(--color-hairline)', color: 'var(--color-mute)', fontFamily: 'var(--font-mono)' }}>
                {nexlyticsMd}
              </pre>
            </div>
          )}
        </div>
      </section>

      {/* ── Footer CTA ── */}
      <section className="py-24" style={{ borderTop: '1px solid var(--color-hairline)' }}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-ink leading-tight" style={{ fontFamily: 'var(--font-display)' }}>
            Build on {engine.name}.
          </h2>
          <p className="mt-5 text-lg leading-relaxed" style={{ color: 'var(--color-mute)' }}>
            Start with Pilot and ship your first micro-SaaS on this engine in 30 days.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/signup?tier=pilot"
              className="inline-flex items-center justify-center px-8 py-4 rounded font-semibold text-base hover:opacity-90 transition-opacity"
              style={{ background: 'var(--color-accent)', color: '#FFFFFF' }}
            >
              Start with Pilot — $149/mo
            </Link>
            <Link
              to="/engines"
              className="inline-flex items-center justify-center px-8 py-4 rounded font-medium text-base transition-colors"
              style={{ border: '1px solid var(--color-hairline)', color: 'var(--color-ink)' }}
            >
              ← All engines
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
